import { Spinner, truncateAddress } from "@gotts.ai/ui";

import type { TxStep } from "../../hooks/useTransactionFlow";

interface TransactionStepperProps {
  steps: TxStep[];
  onRetry?: () => void;
}

export function TransactionStepper({
  steps,
  onRetry,
}: TransactionStepperProps) {
  if (steps.length === 0) return null;

  const completedCount = steps.filter(
    (s) => s.status === "success" || s.status === "skipped",
  ).length;
  const totalSteps = steps.length;

  return (
    <div className="mb-4" data-testid="transaction-stepper">
      {/* Progress counter */}
      <div className="mb-2 flex items-center justify-between">
        <span className="type-field-label">Transaction Progress</span>
        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
          Step {Math.min(completedCount + 1, totalSteps)} of {totalSteps}
        </span>
      </div>

      <div className="relative space-y-0">
        {steps.map((step, i) => {
          const isActive =
            step.status === "pending" || step.status === "confirming";
          const isLast = i === steps.length - 1;

          return (
            <div
              key={i}
              className={`relative flex items-start gap-2.5 py-1.5 ${isActive ? "stepper-step-active" : ""}`}
            >
              {/* Connecting line */}
              {!isLast && (
                <div
                  className={`stepper-line ${
                    step.status === "success" || step.status === "skipped"
                      ? "stepper-line-completed"
                      : "stepper-line-pending"
                  }`}
                  style={{ top: 24, bottom: -6 }}
                />
              )}

              {/* Status indicator */}
              <div className="relative z-10 mt-0.5 flex shrink-0 items-center justify-center">
                {step.status === "idle" && (
                  <span className="inline-block h-5 w-5 rounded-full border border-[var(--glass-border)] bg-transparent opacity-30" />
                )}
                {step.status === "pending" && (
                  <span className="animate-pulse-ring inline-block h-5 w-5 rounded-full border-2 border-[var(--color-gold)] bg-[rgba(201,168,76,0.1)]" />
                )}
                {step.status === "confirming" && <Spinner />}
                {step.status === "success" && (
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-success)] text-xs text-white">
                    &#x2713;
                  </span>
                )}
                {step.status === "skipped" && (
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-text-muted)] text-xs text-white opacity-50">
                    &#x2014;
                  </span>
                )}
                {step.status === "error" && (
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-error)] text-xs text-white">
                    &#x2717;
                  </span>
                )}
              </div>

              {/* Label and hash */}
              <div className="min-w-0 flex-1">
                <span
                  className={`text-xs ${
                    step.status === "success"
                      ? "text-[var(--color-text)] opacity-50"
                      : step.status === "error"
                        ? "text-[var(--color-error)]"
                        : step.status === "skipped"
                          ? "text-[var(--color-text-muted)] opacity-50"
                          : step.status === "pending" ||
                              step.status === "confirming"
                            ? "text-[var(--color-gold)]"
                            : "text-[var(--color-text-muted)]"
                  }`}
                >
                  {step.label}
                </span>
                {step.hash && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    <a
                      href={`/transactions?tx=${step.hash}`}
                      className="font-mono text-[10px] text-[var(--color-text-muted)] underline underline-offset-2 hover:text-[var(--color-gold)]"
                    >
                      {truncateAddress(step.hash)}
                    </a>
                    <button
                      type="button"
                      className="text-[var(--color-text-muted)] opacity-50 hover:opacity-100"
                      onClick={() =>
                        navigator.clipboard.writeText(step.hash ?? "")
                      }
                      title="Copy transaction hash"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <rect x="5" y="5" width="9" height="9" rx="1" />
                        <path d="M3 11V3a1 1 0 011-1h8" />
                      </svg>
                    </button>
                  </span>
                )}
                {step.status === "error" && (
                  <div className="mt-1">
                    {step.error && (
                      <p className="text-[10px] text-[var(--color-error)]">
                        {step.error}
                      </p>
                    )}
                    <div className="mt-1 flex gap-2">
                      {onRetry && (
                        <button
                          type="button"
                          className="btn-percentage-pill text-[var(--color-error)]"
                          onClick={onRetry}
                        >
                          Retry
                        </button>
                      )}
                      {step.hash && (
                        <a
                          href={`#tx-${step.hash}`}
                          className="text-[10px] text-[var(--color-text-muted)] underline underline-offset-2"
                        >
                          View in explorer
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
